import React, { useMemo, useState } from 'react'
import { useStyles } from './styles'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { InputCheck, Button } from 'src/components'
import { PageTitle } from 'src/components/PageTitle'
import { HealthControlForm, healthControlSchema } from 'src/@types/validations/Animal'
import { yupResolver } from '@hookform/resolvers/yup'

import { AnimalHealthControl } from './AnimalHealthControl'
import { LoteHealthControl } from './LoteHealthControl'
import { useHealthControl } from './hooks/useHealthControl'
import { useGetLotList } from 'src/hooks/api/lot'
import { SelectedVacineModal } from './components/SelectedVacineModal'
import { useGetVaccineList } from 'src/hooks/api/vaccine'
import { TAB_LABELS } from './components/VaccineTabs/types'
import { Grid } from '@material-ui/core'
import { AnimalVaccinesHistoricModal } from './components/AnimalVaccinesHistoricModal'

export const defaultFormValues = {
  tipoControle: 'animal',
  dataAplicacao: new Date().toISOString().split('T')[0],
  vacinas: [],
  animais: []
}

export const HealthControl: React.FC = () => {
  const methods = useForm<HealthControlForm>({
    resolver: yupResolver(healthControlSchema),
    defaultValues: defaultFormValues
  })

  return (
    <FormProvider {...methods}>
      <HealthControlComponent />
    </FormProvider>
  )
}

const HealthControlComponent: React.FC = () => {
  const [isOpenSelectedVacineModal, setIsOpenSelectedVacineModal] = useState<boolean>(false)
  const [isOpenAnimalVaccinesHistoricModal, setIsOpenAnimalVaccinesHistoricModal] = useState<boolean>(false)

  const styles = useStyles()

  const {
    watch,
    reset,
    formState: { errors },
    register,
    handleSubmit
  } = useFormContext<HealthControlForm>()

  const { onSubmit, isSaving } = useHealthControl()

  const { data: lotList = [] } = useGetLotList()
  const { data: vacineList } = useGetVaccineList()

  const vaccines = useMemo(() => vacineList || [], [vacineList])

  const controleType = watch('tipoControle')
  const selectedVacines = vaccines.filter((vacine) => watch('vacinas')?.includes(vacine.idVacina!))

  return (
    <>
      <PageTitle title='Controle Sanitário' />

      <form onSubmit={handleSubmit(onSubmit)}>
        <section className={styles.section}>
          <div className={styles.checkInputContainer}>
            <InputCheck
              id='movimentation-animal'
              value='animal'
              label='Animal'
              checked={watch('tipoControle') === 'animal'}
              register={register}
              error={errors?.tipoControle?.message}
              onChange={(e) => {
                const value = e.target.value
                reset({ ...defaultFormValues, tipoControle: value })
              }}
            />

            <InputCheck
              id='movimentation-lote'
              value='lote'
              label='Lote'
              checked={watch('tipoControle') === 'lote'}
              register={register}
              onChange={(e) => {
                const value = e.target.value
                reset({ ...defaultFormValues, tipoControle: value })
              }}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            {controleType === 'animal' && (
              <AnimalHealthControl
                onpenVaccineModal={() => setIsOpenSelectedVacineModal(true)}
                onpenAnimalHistoricModal={() => setIsOpenAnimalVaccinesHistoricModal(true)}
              />
            )}
            {controleType === 'lote' && (
              <LoteHealthControl lotList={lotList} onpenVaccineModal={() => setIsOpenSelectedVacineModal(true)} />
            )}
          </div>

          {selectedVacines.length > 0 && (
            <div className={styles.selectedVacinesContainer}>
              <span>Medicamentos Selecionados:</span>
              <Grid container spacing={2} className={styles.listContainer}>
                {selectedVacines.map((vaccine) => (
                  <Grid key={vaccine.idVacina} item xs={12} sm={6} md={4}>
                    <div className={styles.card} onClick={() => setIsOpenSelectedVacineModal(true)}>
                      <div className={styles.info}>
                        <span>{TAB_LABELS.find((tab) => tab.type === vaccine.data.tipo)?.label || 'Sem tipo'}</span>
                        <span>Descrição: {vaccine.data.descricao || '-'}</span>
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </div>
          )}
        </section>
        <div className={styles.formActions}>
          <Button
            className={styles.buttonWrapper}
            type='submit'
            disabled={isSaving || selectedVacines.length === 0}
            isLoading={isSaving}
          >
            Salvar
          </Button>
        </div>
      </form>
      <SelectedVacineModal
        isOpen={isOpenSelectedVacineModal}
        vacineList={vaccines}
        setIsOpenSelectedVacineModal={setIsOpenSelectedVacineModal}
      />

      <AnimalVaccinesHistoricModal
        isOpen={isOpenAnimalVaccinesHistoricModal}
        idAnimal={watch('idAnimal')!}
        setIsOpenSelectedVacineModal={setIsOpenAnimalVaccinesHistoricModal}
      />
    </>
  )
}
